// EditableTableController — the W5-D4 engine. One instance per grid root;
// owns row/cell state, the roving-tabindex keyboard model (W5-D3), the
// optimistic-commit protocol (W5-D5), row ops (insert/delete/restore), and
// the public data API. Zero transport: every mutation bubbles a
// `ui--work:*` CustomEvent; truth comes back via `ui--work:cell-ack`.
import { Controller } from "@hotwired/stimulus";
import {
  showToast,
  clearToasts,
  adoptOrRollback,
} from "../../../lib/work/mutation_seam";
import { announce, ensureAnnouncer, interpolate } from "../../../lib/work/announce";

export default class EditableTableController extends Controller {
  static values = {
    schema: { type: Array, default: [] },
    rows: { type: Array, default: [] },
    announcements: { type: Object, default: {} },
    virtualizeOver: { type: Number, default: 200 },
  };

  static targets = ["tbody", "rowTemplate", "scroll", "skeleton", "toasts"];

  // === Lifecycle ===

  connect() {
    // Turbo-restoration safety (D1, timeline :95 parity): connect() is
    // idempotent — teardown-then-rebind, so a morph/restore that reconnects
    // the same element never stacks a second copy of a root handler.
    this._teardown();
    this._scanJson();
    this.rowIndex = new Map(); // row_id -> {element, parked: null|Node}
    this.cells = new Map(); // row_id -> Map(field_key -> {value, dirty, editing, snapshot, ackPending})
    this.rows = this.rowsValue.map((r) => String(r.rowId));
    this.tempCounter = 0;
    this.pendingAcks = new Map(); // ackKey -> cell state
    this._rebuildRowRegistry();
    this._assignTabIndexes();
    this._bindRootEvents();
    this._revealSkeleton();
  }

  disconnect() {
    this._teardown();
  }

  // D1 teardown: every listener this controller binds is removed here —
  // the document ack + Escape pair and the four element-level delegated
  // handlers — plus the revert-flash timer and any live toast. Null-safe
  // and silent: the connect-side call on a fresh instance removes nothing.
  _teardown() {
    if (this.documentAckHandler) {
      document.removeEventListener("ui--work:cell-ack", this.documentAckHandler);
      this.documentAckHandler = null;
    }
    if (this.documentKeydownHandler) {
      document.removeEventListener("keydown", this.documentKeydownHandler);
      this.documentKeydownHandler = null;
    }
    if (this._onClick) {
      this.element.removeEventListener("click", this._onClick);
      this._onClick = null;
    }
    if (this._onKeydown) {
      this.element.removeEventListener("keydown", this._onKeydown);
      this._onKeydown = null;
    }
    if (this._onEditorCommit) {
      this.element.removeEventListener("ui--work:editor-commit", this._onEditorCommit);
      this._onEditorCommit = null;
    }
    if (this._onEditorCancel) {
      this.element.removeEventListener("ui--work:editor-cancel", this._onEditorCancel);
      this._onEditorCancel = null;
    }
    if (this.flashTimer) {
      clearTimeout(this.flashTimer);
      this.flashTimer = null;
    }
    if (this.hasToastsTarget) clearToasts(this.toastsTarget);
  }

  // === Hydration ===

  // snake_case → camelCase scan of the emitted rows (one canonical shape).
  // Chip tints live per option (`options[].badge_classes`, resolved
  // server-side) — there is no per-column badgeClasses map.
  _scanJson() {
    this.schema = this.schemaValue.map((c) => ({
      key: c.key,
      type: c.type,
      editor: c.editor || null,
      editable: !!c.editable,
      computed: !!c.computed,
    }));
    this.schemaByKey = new Map(this.schema.map((c) => [c.key, c]));
  }

  // Row registry in DOM order: the keyboard model and the row API walk it.
  _rebuildRowRegistry() {
    this.rowIndex.clear();
    this.cells.clear();
    this.rows = [];
    const realRows = (this.tbodyTarget && !this.tbodyTarget.hidden
      ? this.tbodyTarget
      : this.element.querySelector("tbody:not([hidden])")
    );
    if (!realRows) return;
    realRows.querySelectorAll("tr[data-work-row]").forEach((tr) => {
      if (tr.dataset.workGhost) return;
      const rowId = tr.dataset.workRow;
      this.rowIndex.set(rowId, { element: tr, parked: null });
      this.rows.push(rowId);
      const cells = new Map();
      tr.querySelectorAll("td[data-work-field]").forEach((td) => {
        cells.set(td.dataset.workField, {
          value: this._cellValueFromDom(td),
          dirty: false,
          editing: false,
          snapshot: null,
          ackPending: null,
        });
      });
      this.cells.set(rowId, cells);
    });
  }

  // DOM is display truth; canonical initial values come from the row JSON.
  _cellValueFromDom(td) {
    const row = this.rowsValue.find((r) => String(r.rowId) === td.closest("tr").dataset.workRow);
    return row ? row.cells?.[td.dataset.workField] ?? null : null;
  }

  // === Roving tabindex (W5-D3) ===

  _gridcells() {
    return Array.from(this.element.querySelectorAll("td[data-work-field]"));
  }

  _assignTabIndexes(activeTd) {
    const tds = this._gridcells();
    const active = activeTd || tds[0];
    tds.forEach((td) => {
      td.tabIndex = td === active ? 0 : -1;
    });
  }

  // === Event delegation (root-level; cloned rows participate free) ===

  _bindRootEvents() {
    // Named instance refs (D1): _teardown removes exactly what is bound here.
    this._onClick = (e) => this._handleClick(e);
    this._onKeydown = (e) => this._handleKeydown(e);
    this._onEditorCommit = (e) => this._handleEditorCommit(e);
    this._onEditorCancel = () => this._announce("cancelled");
    this.element.addEventListener("click", this._onClick);
    this.element.addEventListener("keydown", this._onKeydown);
    this.element.addEventListener("ui--work:editor-commit", this._onEditorCommit);
    this.element.addEventListener("ui--work:editor-cancel", this._onEditorCancel);
    this.documentAckHandler = this._onCellAck.bind(this);
    document.addEventListener("ui--work:cell-ack", this.documentAckHandler);
    this.documentKeydownHandler = (e) => this._handleDocumentKeydown(e);
    document.addEventListener("keydown", this.documentKeydownHandler);
  }

  _handleEditorCommit(event) {
    const { row_id, field_key, value, gesture_token } = event.detail || {};
    const cell = this._cellState(row_id, field_key);
    if (!cell) return;
    const entry = this.rowIndex.get(row_id);
    const td = entry && entry.element.querySelector(`td[data-work-field='${CSS.escape(field_key)}']`);
    this._commitCell({ td, rowId: row_id, fieldKey: field_key, value, prevValue: cell.value, gestureToken: gesture_token });
  }

  // Tab commit-and-move from the cell editor (row wrap per W5-D2).
  focusCellAfter(td, delta) {
    const cells = this._gridcells();
    const index = cells.indexOf(td);
    if (index === -1) return;
    const next = cells[index + delta];
    if (next) this._setCursor(next);
  }

  _handleClick(event) {
    const td = event.target.closest("td[data-work-field]");
    if (td) {
      this._setCursor(td);
      return;
    }
    // Insert intent (the add-row button).
    if (event.target.closest("[data-action*='requestInsert']")) return; // handled by its own action
  }

  // === Cursor + keyboard model (W5-D3) ===

  _handleKeydown(event) {
    const td = event.target.closest("td[data-work-field]");
    if (!td || this._inEditor(event.target)) return; // editor owns its keys
    const key = event.key;

    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(key)) {
      event.preventDefault();
      this._move(td, key);
    } else if (key === "Home" || key === "End") {
      event.preventDefault();
      const cells = this._rowCells(td);
      this._setCursor(key === "Home" ? cells[0] : cells[cells.length - 1]);
    } else if (key === "Enter" || key === "F2") {
      event.preventDefault();
      this._openEditor(td);
    } else if (key === " ") {
      // Space activates checkbox cells; otherwise scrolls — take it on cells.
      if (this._editorKeyOf(td) === "checkbox") {
        event.preventDefault();
        this._toggleCheckbox(td);
      } else {
        event.preventDefault();
        this._openEditor(td);
      }
    } else if (key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      // Printable character prefill (W5-D3): replace-style edit start.
      this._openEditor(td, { prefill: key });
    }
  }

  _inEditor(target) {
    return !!target.closest("[data-work-editor-root], [data-ui--work-cell-editor-target='display']");
  }

  _rowCells(td) {
    return Array.from(td.closest("tr").querySelectorAll("td[data-work-field]"));
  }

  _setCursor(td) {
    this._assignTabIndexes(td);
    td.focus();
  }

  _move(td, key) {
    const rtl = getComputedStyle(this.element).direction === "rtl";
    const cells = this._rowCells(td);
    const rowIndex = this.rows.indexOf(td.closest("tr").dataset.workRow);
    const colIndex = cells.indexOf(td);
    let next = null;
    if (key === "ArrowUp") next = this._cellAt(rowIndex - 1, colIndex);
    if (key === "ArrowDown") next = this._cellAt(rowIndex + 1, colIndex);
    if (key === "ArrowLeft") next = cells[rtl ? colIndex + 1 : colIndex - 1] || null;
    if (key === "ArrowRight") next = cells[rtl ? colIndex - 1 : colIndex + 1] || null;
    if (next) this._setCursor(next); // clamped at the edge — no wrap
  }

  _cellAt(rowIndex, colIndex) {
    if (rowIndex < 0 || rowIndex >= this.rows.length) return null;
    const row = this.rowIndex.get(this.rows[rowIndex]);
    if (!row) return null;
    const cells = Array.from(row.element.querySelectorAll("td[data-work-field]"));
    return cells[colIndex] || null;
  }

  _editorKeyOf(td) {
    return td.dataset.workEditor || null;
  }

  // === Editor open / commit protocol (W5-D2 + W5-D5) ===

  // Opens the cell editor for td. `opts.prefill` seeds a replace-style edit
  // (printable character gesture). Announces "editing" for non-checkbox.
  _openEditor(td, opts = {}) {
    const editorRoot = td.querySelector("[data-controller~='ui--work-cell-editor']");
    if (!editorRoot) return this._announceCannotEdit(td);
    const ctrl = this.application.getControllerForElementAndIdentifier(
      editorRoot, "ui--work-cell-editor");
    if (!ctrl) return;
    if (this._editorKeyOf(td) === "checkbox") return this._toggleCheckbox(td);
    ctrl.openFromGrid({ prefill: opts.prefill });
    this._announce("editing", { field: td.dataset.workField, row: td.closest("tr").dataset.workRow });
  }

  _announceCannotEdit(td) {
    this._announce("cannot_edit", { field: td.dataset.workField });
  }

  _toggleCheckbox(td) {
    const input = td.querySelector("input[type='checkbox']");
    if (!input) return;
    input.checked = !input.checked;
    const rowId = td.closest("tr").dataset.workRow;
    const fieldKey = td.dataset.workField;
    const prev = !input.checked;
    // Token-stamped like checkboxChanged: under the strict ack guard every
    // commit must be resolvable by the token it emitted.
    this._commitCell({ td, rowId, fieldKey, value: input.checked, prevValue: prev, gestureToken: this._token() });
  }

  // Engine-side commit: snapshot → optimistic value + dirty → exactly one
  // ui--work:cell-commit per gesture. Single-flight: only the LATEST
  // commit is ack-resolvable; an earlier in-flight commit that resolves
  // after a newer one is stale (see _onCellAck). Coalescing: a commit
  // while one is pending merely refreshes the pending value.
  _commitCell({ td, rowId, fieldKey, value, prevValue, gestureToken }) {
    const cell = this._cellState(rowId, fieldKey);
    if (!cell) return;
    if (!cell.dirty) cell.snapshot = cell.value; // first optimistic change holds the revert base
    cell.ackPending = { value, prevValue, gestureToken, td };
    cell.dirty = true;
    cell.value = value; // optimistic truth until the ack resolves
    td.classList.add("bg-amber-50", "dark:bg-amber-900/30");
    this._emit("ui--work:cell-commit", {
      row_id: rowId,
      field_key: fieldKey,
      value,
      prev_value: prevValue,
      gesture_token: gestureToken,
    });
  }

  _cellState(rowId, fieldKey) {
    const cellMap = this.cells.get(rowId);
    return cellMap ? cellMap.get(fieldKey) : null;
  }

  // Document-level ack listener: host resolution for one pending commit.
  _onCellAck(event) {
    const { row_id, field_key, gesture_token, status } = event.detail || {};
    const cell = this._cellState(row_id, field_key);
    if (!cell || !cell.ackPending) return;
    if (typeof gesture_token !== "string" || cell.ackPending.gestureToken !== gesture_token) {
      // Stale rollback guard: an old gesture's revert must not clobber a
      // newer optimistic value — tell the host to refetch instead. A
      // TOKENLESS ack matches nothing: addressing a pending gesture
      // requires the token that commit emitted.
      this._emit("ui--work:cell-stale", { row_id, field_key });
      return;
    }
    const { td } = cell.ackPending;
    const ack = { ok: status === "adopted" };
    adoptOrRollback({
      ack,
      region: this.hasToastsTarget ? this.toastsTarget : null, // the seam no-ops on a falsy region
      applyAdopt: () => {
        cell.dirty = false;
        cell.ackPending = null;
        td.classList.remove("bg-amber-50", "dark:bg-amber-900/30");
        return "adopted";
      },
      applyRevert: () => {
        cell.value = cell.snapshot != null ? cell.snapshot : cell.value;
        cell.dirty = false;
        cell.ackPending = null;
        td.classList.remove("bg-amber-50", "dark:bg-amber-900/30");
        this._paintCell(td, cell.value);
        this._flashRevert(td);
        return "reverted";
      },
      errorMessage: ack.ok ? undefined : this._announce("reverted", { field: field_key }, {}),
    });
  }

  // Paint a canonical value back into the cell's display (no innerHTML of
  // host content — text via textContent; chips rebuilt from schema options
  // with createElement/textContent only).
  _paintCell(td, value) {
    const column = this.schemaByKey.get(td.dataset.workField);
    const display = td.querySelector("[data-ui--work-cell-editor-target='display']");
    if (!display) return;
    if (column && (column.type === "single_select" || column.type === "multi_select")) {
      display.replaceChildren(...this._chipsMarkup(column, value));
    } else {
      display.replaceChildren(document.createTextNode(value == null ? "" : String(value)));
    }
  }

  // Revert flash (docs/work/theming.md, wave 5): the restored cell paints
  // the danger-wash pair for a beat so the failed commit is visible, then
  // clears. One timer per grid — a newer revert re-arms it on its cell and
  // clears any stale flash first.
  _flashRevert(td) {
    if (this.flashTimer) clearTimeout(this.flashTimer);
    this.element.querySelectorAll(".bg-danger\\/10").forEach((el) => {
      el.classList.remove("bg-danger/10", "dark:bg-danger/20");
    });
    td.classList.add("bg-danger/10", "dark:bg-danger/20");
    this.flashTimer = setTimeout(() => {
      td.classList.remove("bg-danger/10", "dark:bg-danger/20");
      this.flashTimer = null;
    }, 1600);
  }

  _chipsMarkup(column, values) {
    const keys = Array.isArray(values) ? values : [values];
    const chips = [];
    keys.forEach((key) => {
      const opt = this._optionOf(column, key);
      if (!opt) return;
      const chip = document.createElement("span");
      chip.className = `inline-flex max-w-full items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium ${opt.badge_classes || ""}`;
      if (opt.emoji) {
        const emoji = document.createElement("span");
        emoji.setAttribute("aria-hidden", "true");
        emoji.textContent = opt.emoji;
        chip.appendChild(emoji);
      }
      const label = document.createElement("span");
      label.className = "truncate";
      label.textContent = opt.label;
      chip.appendChild(label);
      chips.push(chip);
    });
    return chips;
  }

  _optionOf(column, key) {
    const col = this.schemaValue.find((c) => c.key === column.key) || {};
    return (col.options || []).find((o) => String(o.key) === String(key)) || null;
  }

  // Document-level Escape: cancel the active editor (no commit event —
  // W5-D2; the editor's `ui--work:editor-cancel` still bubbles, which is
  // what the announcement below listens for). The cell-editor controller
  // also listens on its own root; whoever sees it first cancels and the
  // other becomes a no-op.
  _handleDocumentKeydown(event) {
    if (event.key !== "Escape") return;
    const root = this.element.querySelector(
      "[data-ui--work-cell-editor-target='display'][data-work-editing='true']"
    );
    if (!root) return;
    const editorRoot = root.closest("[data-controller~='ui--work-cell-editor']");
    const ctrl = editorRoot && this.application.getControllerForElementAndIdentifier(
      editorRoot, "ui--work-cell-editor");
    if (ctrl) ctrl.cancelFromGrid();
  }

  // === Announcements + events ===

  _announce(key, tokens = {}, opts = {}) {
    const template = this.announcementsValue[key] || this._defaultAnnouncement(key);
    if (!template) return "";
    const message = interpolate(template, tokens);
    if (opts.toast) {
      if (this.hasToastsTarget) showToast(this.toastsTarget, message, "error");
    } else {
      announce(ensureAnnouncer(this.element), template, tokens);
    }
    return message;
  }

  _defaultAnnouncement(key) {
    const defaults = {
      editing: "Editing {field} in row {row}",
      cannot_edit: "This cell cannot be edited",
      cancelled: "Edit cancelled",
      row_added: "Row added",
      row_removed: "Row removed",
      reverted: "Changes could not be saved",
    };
    return defaults[key] || "";
  }

  _emit(eventName, detail) {
    this.element.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      cancelable: true,
      detail,
    }));
  }

  // === Row operations (W5-D4 data API) ===

  // Optimistic insert: clone the ghost template, give it a temp row id,
  // park it after `afterRowId` (or top when null). Emits ui--work:row-insert
  // exactly once; the host's ack assigns the real id via replaceRows.
  insertRow(afterRowId = null) {
    const template = this.rowTemplateTarget;
    const clone = template.content.firstElementChild.cloneNode(true);
    const tempId = this._nextTempId();
    clone.dataset.workRow = tempId;
    clone.removeAttribute("data-work-ghost");
    clone.removeAttribute("aria-hidden");
    // The ghost rendered with row_id: "" — re-stamp so an edit in the
    // inserted row commits with the temp id, not an empty row_id
    // (_handleEditorCommit silently drops unknown rows). The editor roots
    // commit through their data-ui--work-cell-editor-row-id-value
    // attribute (Stimulus re-reads value attributes live); the row-menu
    // delete button reads its own data-work-row in requestDelete. The
    // checkbox adapter needs no stamp of its own — checkboxChanged walks
    // input.closest("tr[data-work-row]"), and the tr is stamped via
    // clone.dataset.workRow above.
    clone.querySelectorAll("[data-ui--work-cell-editor-row-id-value]").forEach((el) => {
      el.setAttribute("data-ui--work-cell-editor-row-id-value", tempId);
    });
    clone.querySelectorAll("[data-work-row]").forEach((el) => {
      el.setAttribute("data-work-row", tempId);
    });
    const tbody = this.tbodyTarget;
    if (afterRowId) {
      const anchor = this.rowIndex.get(afterRowId);
      if (anchor && anchor.element.parentNode === tbody) {
        anchor.element.after(clone);
      } else {
        tbody.prepend(clone);
      }
    } else {
      tbody.prepend(clone);
    }
    this.rowIndex.set(tempId, { element: clone, parked: null, anchor: null });
    this.cells.set(tempId, new Map());
    const at = afterRowId ? this.rows.indexOf(afterRowId) : -1;
    this.rows.splice(at + 1, 0, tempId);
    this._registerCells(tempId, clone);
    this._assignTabIndexes();
    this._emit("ui--work:row-insert", { temp_id: tempId, after_row_id: afterRowId });
    this._announce("row_added");
    return tempId;
  }

  _nextTempId() {
    this.tempCounter += 1;
    return `temp-${Date.now()}-${this.tempCounter}`;
  }

  _registerCells(rowId, tr) {
    const cells = new Map();
    tr.querySelectorAll("td[data-work-field]").forEach((td) => {
      cells.set(td.dataset.workField, {
        value: null, dirty: false, editing: false, snapshot: null, ackPending: null,
      });
    });
    this.cells.set(rowId, cells);
  }

  // Optimistic delete: park the row node (never re-render), remove from the
  // registry, emit ui--work:row-delete once. If the host's revert path
  // calls restoreRow, the parked node returns at its original position.
  removeRow(rowId) {
    const entry = this.rowIndex.get(String(rowId));
    if (!entry) return false;
    entry.parked = entry.element;
    entry.anchor = entry.element.nextSibling;
    entry.element.remove();
    this.rows = this.rows.filter((id) => id !== String(rowId));
    this._assignTabIndexes();
    this._emit("ui--work:row-delete", { row_id: String(rowId) });
    this._announce("row_removed");
    return true;
  }

  // Undo-side: reattach a parked node at its original position.
  restoreRow(rowId) {
    const entry = this.rowIndex.get(String(rowId));
    if (!entry || !entry.parked) return false;
    // The anchor row may itself have been removed while this row sat
    // parked (parentNode null) — fall back to the tbody end instead of
    // throwing a TypeError.
    if (entry.anchor && entry.anchor.parentNode) {
      entry.anchor.parentNode.insertBefore(entry.parked, entry.anchor);
    } else {
      this.tbodyTarget.appendChild(entry.parked);
    }
    entry.parked = null;
    this.rows.push(String(rowId));
    this.rows.sort((a, b) => this._domOrder(a) - this._domOrder(b));
    this._assignTabIndexes();
    return true;
  }

  _domOrder(rowId) {
    const entry = this.rowIndex.get(rowId);
    if (!entry) return 0;
    const rows = Array.from(this.tbodyTarget.querySelectorAll("tr[data-work-row]"));
    return rows.indexOf(entry.element);
  }

  // Host ack for row-insert: swap temp id → real row id in one pass.
  replaceRows() {
    this._rebuildRowRegistry();
    this._assignTabIndexes();
  }

  // === Public data API (W5-D4; the W6 sibling depends on this shape) ===

  rowIds() {
    return this.rows.slice();
  }

  getCell(rowId, fieldKey) {
    const cell = this._cellState(String(rowId), fieldKey);
    return cell ? cell.value : undefined;
  }

  // Programmatic set: same optimistic protocol as a gesture (dirty + one
  // commit event + ack resolution), minus the popover.
  setCell(rowId, fieldKey, value) {
    const cell = this._cellState(String(rowId), fieldKey);
    const tr = this.rowIndex.get(String(rowId))?.element;
    const td = tr && tr.querySelector(`td[data-work-field='${CSS.escape(fieldKey)}']`);
    if (!cell || !td) return false;
    const prev = cell.value;
    cell.snapshot = cell.snapshot != null ? cell.snapshot : prev;
    this._commitCell({ td, rowId: String(rowId), fieldKey, value, prevValue: prev, gestureToken: this._token() });
    return true;
  }

  // Host-declared failure: revert the pending commit to its snapshot.
  revertCell(rowId, fieldKey, gestureToken) {
    const cell = this._cellState(String(rowId), fieldKey);
    if (!cell || !cell.ackPending) return false;
    if (gestureToken && cell.ackPending.gestureToken !== gestureToken) {
      this._emit("ui--work:cell-stale", { row_id: String(rowId), field_key: fieldKey });
      return false;
    }
    const { td, value } = cell.ackPending;
    cell.value = cell.snapshot != null ? cell.snapshot : value;
    cell.dirty = false;
    cell.ackPending = null;
    td.classList.remove("bg-amber-50", "dark:bg-amber-900/30");
    const tr = this.rowIndex.get(String(rowId))?.element;
    const td2 = tr && tr.querySelector(`td[data-work-field='${CSS.escape(fieldKey)}']`);
    if (td2) {
      this._paintCell(td2, cell.value);
      this._flashRevert(td2);
    }
    this._announce("reverted", { field: fieldKey }, { toast: true });
    return true;
  }

  // Host-declared success (explicit API alternative to the cell-ack event).
  acceptCellTruth(rowId, fieldKey, truth) {
    const cell = this._cellState(String(rowId), fieldKey);
    if (!cell) return false;
    cell.value = truth;
    cell.dirty = false;
    cell.ackPending = null;
    const tr = this.rowIndex.get(String(rowId))?.element;
    const td = tr && tr.querySelector(`td[data-work-field='${CSS.escape(fieldKey)}']`);
    if (td) {
      td.classList.remove("bg-amber-50", "dark:bg-amber-900/30");
      this._paintCell(td, truth);
    }
    return true;
  }

  _token() {
    this._tokenCounter = (this._tokenCounter || 0) + 1;
    return `g-${Date.now()}-${this._tokenCounter}`;
  }

  // === Delegated actions (checkbox adapter, add-row button, row menu) ===

  // The instant-commit exception (W5-D2): change on a checkbox adapter
  // commits in one gesture — snapshot → optimistic → one cell-commit.
  checkboxChanged(event) {
    const input = event.target;
    if (!input.matches("input[type='checkbox']")) return;
    const tr = input.closest("tr[data-work-row]");
    const td = input.closest("td[data-work-field]");
    if (!tr || !td) return;
    const rowId = tr.dataset.workRow;
    const fieldKey = td.dataset.workField;
    const cell = this._cellState(rowId, fieldKey);
    if (!cell) return;
    this._commitCell({
      td, rowId, fieldKey,
      value: input.checked,
      prevValue: !input.checked,
      gestureToken: this._token(),
    });
  }

  requestInsert(event) {
    event.preventDefault();
    this.insertRow(null);
  }

  requestDelete(event) {
    const rowId = event.currentTarget.dataset.workRow;
    if (rowId) this.removeRow(rowId);
  }

  // === Loading state (skeleton reveal) ===

  _revealSkeleton() {
    if (!this.hasSkeletonTarget) return;
    const skeleton = this.skeletonTarget;
    const tbody = this.tbodyTarget;
    if (!skeleton || !tbody || !skeleton.hidden) return;
    // Still loading on connect: keep the skeleton; hydration continues on
    // the hidden tbody. The host flips `loading` by replacing the fragment.
  }
}

